// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import { array, string } from 'prop-types';
import styled from '@emotion/styled';
import { Accordion, AccordionItem } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import {
  UserRound,
  LayoutDashboard,
  ShoppingCart,
  Heart,
  Package,
  FileText,
  MapPinHouse,
  UsersRound,
  ChartNoAxesCombined,
  Combine,
  SquareCode,
  Info,
  ChevronRight,
} from 'lucide-react';

import usePermissions from 'Hooks/usePermissions';
import useUser from 'Hooks/useUser';
import { Can } from 'Components/shared';
import { PERMISSION } from 'Constants';

const StyledNavBar = styled.aside`
  background-color: #F5F5F5;
  max-width: 260px;
  width: 100%;
  height: fit-content;

  > ul > li {
    &:hover {
      background-color: transparent !important;
    }
    &:last-of-type {
      border-bottom: 0 none;
      margin-bottom: 5px;
    }
    width: 90%;
    margin: 0 auto;
    text-align: left;
    border-top: 0;
    border-bottom: 1px solid ${ props => props.theme.products.colors.border };

    .cds--accordion__arrow {
      display: none !important;
    }

    .accordion-title-wrapper {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      .lucide-icon {
        transition: transform 0.11s cubic-bezier(0.2, 0, 0.38, 0.9);
      }
    }

    .cds--accordion__heading {
      padding: 0;

      &:hover {
        background: transparent;

        &::before {
          display: none;
        }
      }

      &:focus::before {
        outline: 0 none;
        border: 0;
      }
    }

    &.cds--accordion__item--active {
      .lucide-icon {
        transform: rotate(90deg);
      }

      .cds--accordion__title {
        font-weight: 600;
        color: ${ props => props.theme.colors.altOrange };
      }
    }

    .cds--accordion__title {
      position: relative;
      margin: 0;
      height: 50px;
      width: 100%;
      display: inline-flex;
      align-items: center;
      line-height: 50px;
      padding-left: 0;

      .accordion-title-wrapper > div {
        display: flex;
        align-items: center;
      }

      .icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        margin-right: 10px;
      }

      svg {
        margin: 0;
      }
    }

    > .cds--accordion__title {
      > a {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        height: 100%;
        width: 100%;
      }
    }
  }

  li.active {
    a, button {
      color: ${ props => props.theme.colors.altOrange } !important;
    }
  }

  li a,
  li button {
    cursor: pointer;
    height: 100%;
    font-size: 0.875rem;
    text-align: left;
    color: ${ props => props.theme.colors.darkGray };
  }

  .cds--accordion__content {
    padding: 0 !important;

    ul {
      max-height: 150px;
      overflow: auto;

      li {
        margin: 0 0 12px 55px;

        a, button {
          display: block;
          width: 100%;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          margin: 0;
          padding: 0;
          font-size: 0.875rem;
          color: ${ props => props.theme.colors.darkGray };
          text-align: left;
          min-height: 10px;
          height: auto;
        }
        
        button {
          font-weight: 600;
        }
      }
    }
  }
`;

const AccountSideMenu = props => {
  const { lists, selectedListId, defaultOpen, defaultActive } = props;
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useUser().data || {};
  const { isImpersonated } = user;
  const { isInternalReseller, canAccessAccountStatements } = usePermissions();
  return (
    <StyledNavBar theme={ theme } data-card-list data-floating-menu-container>
      <Accordion>
        <li className={ `cds--accordion__item ${ defaultActive === '/' ? 'active' : '' }` }>
          <div className="cds--accordion__title">
            <Link to="/">
              <span className="icon">
                <LayoutDashboard />
              </span>
              { t('users.menu.dashboard') }
            </Link>
          </div>
        </li>
        <AccordionItem
          open={ defaultOpen === 'account' }
          title={ (
            <div className="accordion-title-wrapper">
              <div>
                <span className="icon">
                  <UserRound />
                </span>
                { t('users.menu.account') }
              </div>
              <ChevronRight className="lucide-icon" size={ 24 } />
            </div>
          ) }
        >
          <ul>
            <li className={ defaultActive === '/account/profile' ? 'active' : '' }>
              <Link to="/account/profile">
                { t('users.menu.profile') }
              </Link>
            </li>
            <li className={ defaultActive === '/account/settings' ? 'active' : '' }>
              <Link to="/account/settings">
                { t('users.menu.settings') }
              </Link>
            </li>
            <li className={ defaultActive === '/account/personal-data' ? 'active' : '' }>
              <Link to="/account/personal-data">
                { t('users.menu.personalData') }
              </Link>
            </li>
            { !isImpersonated && (
              <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
                <li className={ defaultActive === '/account/company-data' ? 'active' : '' }>
                  <Link to="/account/company-data">
                    { t('offers.companyDataTab') }
                  </Link>
                </li>
              </Can>
            ) }
          </ul>
        </AccordionItem>
        <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
          <li className={ `cds--accordion__item ${ defaultActive === '/orders' ? 'active' : '' }` }>
            <div className="cds--accordion__title">
              <Link to="/orders">
                <span className="icon">
                  <ShoppingCart />
                </span>
                { t('users.menu.orders') }
              </Link>
            </div>
          </li>
          { isInternalReseller && (
            <li className={ `cds--accordion__item ${ defaultActive === '/orders/reserved-products' ? 'active' : '' }` }>
              <div className="cds--accordion__title">
                <Link to="/orders/reserved-products">
                  <span className="icon">
                    <Package />
                  </span>
                  { t('users.menu.productsReservations') }
                </Link>
              </div>
            </li>
          ) }
        </Can>
        { !isImpersonated && (
          <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
            <li className={ `cds--accordion__item ${ defaultActive.startsWith('/offers') ? 'active' : '' }` }>
              <div className="cds--accordion__title">
                <Link to="/offers">
                  <span className="icon"><FileText /></span>
                  { t('offers.myOffers') }
                </Link>
              </div>
            </li>
          </Can>
        ) }
        <AccordionItem
          open={ defaultOpen === 'lists' }
          title={ (
            <div className="accordion-title-wrapper">
              <div>
                <span className="icon">
                  <Heart />
                </span>
                { t('users.menu.lists') }
              </div>
              <ChevronRight className="lucide-icon" size={ 24 } />
            </div>
          ) }
        >
          <ul>
            { lists.map(list => {
              if (list?.id) {
                return (
                  <li key={ list.id } className={ list.id === selectedListId ? 'active' : '' }>
                    <Link to={ `/lists/${list.id}` }>
                      { list.name }
                    </Link>
                  </li>
                );
              }
              return null;
            }) }
            {/*<li>*/}
            {/*  <Button onClick={ onModalOpen } kind="ghost">*/}
            {/*    + { t('wishlists.newList') }*/}
            {/*  </Button>*/}
            {/*</li>*/}
          </ul>
        </AccordionItem>
        <Can permission={ PERMISSION.MANAGE_ADDRESSES }>
          <li className={ `cds--accordion__item ${ defaultActive === '/addresses' ? 'active' : '' }` }>
            <div className="cds--accordion__title">
              <Link to="/addresses">
                <span className="icon"><MapPinHouse /></span>
                { t('users.menu.addresses') }
              </Link>
            </div>
          </li>
        </Can>
        <Can permission={ PERMISSION.MANAGE_USERS }>
          <li className={ `cds--accordion__item ${ defaultActive === '/users' ? 'active' : '' }` }>
            <div className="cds--accordion__title">
              <Link to="/users">
                <span className="icon"><UsersRound /></span>
                { t('users.menu.users') }
              </Link>
            </div>
          </li>
        </Can>
        <Can permission={ PERMISSION.ACCESS_ACCOUNT_FEATURES }>
          <AccordionItem
            open={ defaultOpen === 'reports' }
            title={ (
              <div className="accordion-title-wrapper">
                <div>
                  <span className="icon">
                    <ChartNoAxesCombined />
                  </span>
                  { t('users.menu.reports') }
                </div>
                <ChevronRight className="lucide-icon" size={ 24 } />
              </div>
            ) }
          >
            <ul>
              <li className={ defaultActive === '/reports/invoices' ? 'active' : '' }>
                <Link to="/reports/invoices">
                  { t('users.menu.invoices') }
                </Link>
              </li>
              <li className={ defaultActive === '/reports/unpaid-invoices' ? 'active' : '' }>
                <Link to="/reports/unpaid-invoices">
                  { t('users.menu.unpaidInvoices') }
                </Link>
              </li>
              <li className={ defaultActive === '/reports/balance' ? 'active' : '' }>
                <Link to="/reports/balance">
                  { t('users.menu.balanceDocuments') }
                </Link>
              </li>
              <li className={ defaultActive === '/reports/payments' ? 'active' : '' }>
                <Link to="/reports/payments">
                  { t('users.menu.payments') }
                </Link>
              </li>
              { canAccessAccountStatements && (
                <li className={ defaultActive === '/reports/account-statements' ? 'active' : '' }>
                  <Link to="/reports/account-statements">
                    { t('users.menu.accountStatements') }
                  </Link>
                </li>
              ) }
            </ul>
          </AccordionItem>
          <AccordionItem
            open={ defaultOpen === 'service' }
            title={ (
              <div className="accordion-title-wrapper">
                <div>
                  <span className="icon">
                    <Combine />
                  </span>
                  { t('users.menu.service') }
                </div>
                <ChevronRight className="lucide-icon" size={ 24 } />
              </div>
            ) }
          >
            <ul>
              <li className={ defaultActive === '/service/rma' ? 'active' : '' }>
                <Link to="/service/rma">
                  { t('users.menu.rma') }
                </Link>
              </li>
              <li className={ defaultActive === '/service/warranties' ? 'active' : '' }>
                <Link to="/service/warranties">
                  { t('users.menu.warranties') }
                </Link>
              </li>
              <li className={ defaultActive.startsWith('/service/ppwr') ? 'active' : '' }>
                <Link to="/service/ppwr">
                  { t('ppwr.heading') }
                </Link>
              </li>
            </ul>
          </AccordionItem>
          <AccordionItem
            open={ defaultOpen === 'api-integrations' }
            title={ (
              <div className="accordion-title-wrapper">
                <div>
                  <span className="icon">
                    <SquareCode />
                  </span>
                  { t('users.menu.api') }
                </div>
                <ChevronRight className="lucide-icon" size={ 24 } />
              </div>
            ) }
          >
            <ul>
              <li className={ defaultActive === '/api-integrations/feeds' ? 'active' : '' }>
                <Link to="/api-integrations/feeds">
                  { t('users.menu.apiFeeds') }
                </Link>
              </li>
              <li className={ defaultActive === '/api-integrations/api-information' ? 'active' : '' }>
                <Link to="/api-integrations/api-information">
                  { t('users.menu.apiInformation') }
                </Link>
              </li>
              <li className={ defaultActive === '/api-integrations/ecommerce-tools' ? 'active' : '' }>
                <Link to="/api-integrations/ecommerce-tools">
                  { t('users.menu.ecommerceTools') }
                </Link>
              </li>
            </ul>
          </AccordionItem>
          <li className={ `cds--accordion__item ${ defaultActive === '/account/info' ? 'active' : '' }` }>
            <div className="cds--accordion__title">
              <Link to="/account/info">
                <span className="icon"><Info size={ 18 } /></span>
                { t('users.menu.info') }
              </Link>
            </div>
          </li>
        </Can>
      </Accordion>
    </StyledNavBar>
  );
};

AccountSideMenu.propTypes = {
  lists: array,
  defaultOpen: string,
  defaultActive: string,
  selectedListId: string,
};
AccountSideMenu.displayName = 'AccountSideMenuComponent';

export default AccountSideMenu;
